import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Archive, ArchiveRestore, Eye, EyeOff, ExternalLink, LogOut, Pencil, Plus, Star, StarOff, Trash2, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { adminFetch, adminUpload, clearToken, getToken } from "@/admin/api";
import { mediaUrl } from "@/lib/mediaUrl";

const TEXT = {
  ar: {
    title: "إدارة المشاريع", subtitle: "أضف أعمالك وانشرها من هنا", resources: "الموارد", site: "عرض الموقع", logout: "خروج",
    add: "مشروع جديد", empty: "لا توجد مشاريع بعد.", loading: "جارٍ التحميل...", edit: "تعديل المشروع", new: "مشروع جديد",
    slug: "رابط المشروع المختصر", slugHint: "حروف إنجليزية صغيرة وأرقام وشرطة فقط", cover: "صورة الغلاف", gallery: "صور إضافية",
    upload: "رفع صورة", uploadMore: "إضافة صور", external: "رابط التجربة أو الموقع", titleField: "اسم المشروع",
    type: "نوع المشروع", summary: "وصف مختصر", problem: "المشكلة", solution: "الحل", outcome: "النتيجة",
    published: "منشور", draft: "مسودة", featured: "مميز", archived: "مؤرشف", save: "حفظ المشروع", saving: "جارٍ الحفظ...",
    cancel: "إلغاء", saved: "حُفظ المشروع", updated: "حُدّث المشروع", deleted: "حُذف المشروع", confirm: "هل تريد حذف هذا المشروع نهائيًا؟",
    tooLarge: "الحد الأقصى للصورة 8 ميغابايت", removeImage: "إزالة الصورة", required: "الاسم بالعربية والإنجليزية مطلوب.",
  },
  en: {
    title: "Projects", subtitle: "Add and publish your work here", resources: "Resources", site: "View site", logout: "Log out",
    add: "New project", empty: "No projects yet.", loading: "Loading...", edit: "Edit project", new: "New project",
    slug: "Project URL slug", slugHint: "Lowercase English letters, numbers, and hyphens only", cover: "Cover image", gallery: "More images",
    upload: "Upload image", uploadMore: "Add images", external: "Live project URL", titleField: "Project name",
    type: "Project type", summary: "Short summary", problem: "Problem", solution: "Solution", outcome: "Outcome",
    published: "Published", draft: "Draft", featured: "Featured", archived: "Archived", save: "Save project", saving: "Saving...",
    cancel: "Cancel", saved: "Project saved", updated: "Project updated", deleted: "Project deleted", confirm: "Delete this project permanently?",
    tooLarge: "Images must be 8 MB or smaller", removeImage: "Remove image", required: "Arabic and English names are required.",
  },
  fr: {
    title: "Projets", subtitle: "Ajoutez et publiez vos projets ici", resources: "Ressources", site: "Voir le site", logout: "Déconnexion",
    add: "Nouveau projet", empty: "Aucun projet pour l'instant.", loading: "Chargement...", edit: "Modifier le projet", new: "Nouveau projet",
    slug: "Adresse du projet", slugHint: "Lettres anglaises minuscules, chiffres et tirets", cover: "Image de couverture", gallery: "Autres images",
    upload: "Ajouter une image", uploadMore: "Ajouter des images", external: "Lien du projet", titleField: "Nom du projet",
    type: "Type de projet", summary: "Résumé", problem: "Problème", solution: "Solution", outcome: "Résultat",
    published: "Publié", draft: "Brouillon", featured: "À la une", archived: "Archivé", save: "Enregistrer", saving: "Enregistrement...",
    cancel: "Annuler", saved: "Projet enregistré", updated: "Projet modifié", deleted: "Projet supprimé", confirm: "Supprimer définitivement ce projet ?",
    tooLarge: "Image limitée à 8 Mo", removeImage: "Retirer l'image", required: "Les noms arabe et anglais sont requis.",
  },
};

const LANGUAGES = [{ id: "ar", label: "العربية" }, { id: "en", label: "English" }, { id: "fr", label: "Français" }];
const LOCAL_FIELDS = ["title", "type", "statement", "problem", "solution", "outcome"];
const EMPTY = {
  slug: "", cover_url: "", gallery: [], external_url: "", published: false, featured: false, archived: false,
  ...Object.fromEntries(LOCAL_FIELDS.flatMap((field) => LANGUAGES.map(({ id }) => [`${field}_${id}`, ""]))),
};
const toForm = (project) => Object.fromEntries(Object.keys(EMPTY).map((key) => [key, project?.[key] ?? EMPTY[key]]));
const inputClass = "w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-charcoal outline-none focus:border-sage";
const buttonClass = "inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm text-graphite hover:border-sage";
const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const AdminProjects = () => {
  const { lang } = useLanguage();
  const t = TEXT[lang];
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editLang, setEditLang] = useState("ar");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminFetch("/admin/projects");
      setProjects(data.projects || []);
    } catch (error) {
      if (error.message !== "Unauthorized") {
        toast.error(error.message);
        setProjects([]);
      }
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const openNew = () => { setForm(toForm(null)); setEditLang("ar"); setModal({ mode: "create" }); };
  const openEdit = (project) => { setForm(toForm(project)); setEditLang("ar"); setModal({ mode: "edit", project }); };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title_ar.trim() || !form.title_en.trim()) {
      toast.error(t.required);
      return;
    }
    setSaving(true);
    try {
      const path = modal.mode === "create" ? "/admin/projects" : `/admin/projects/${modal.project.id}`;
      await adminFetch(path, { method: modal.mode === "create" ? "POST" : "PUT", body: JSON.stringify(form) });
      toast.success(modal.mode === "create" ? t.saved : t.updated);
      setModal(null);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const patch = async (project, changes) => {
    try {
      await adminFetch(`/admin/projects/${project.id}`, { method: "PUT", body: JSON.stringify({ ...toForm(project), ...changes }) });
      toast.success(t.updated);
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const remove = async (project) => {
    if (!window.confirm(t.confirm)) return;
    try {
      await adminFetch(`/admin/projects/${project.id}`, { method: "DELETE" });
      toast.success(t.deleted);
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const upload = async (event, kind) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (files.some((file) => file.size > 8 * 1024 * 1024)) {
      toast.error(t.tooLarge);
      return;
    }
    setUploading(true);
    try {
      if (kind === "cover") {
        const result = await adminUpload(files[0]);
        setForm((current) => ({ ...current, cover_url: result.url }));
      } else {
        const urls = [];
        for (const file of files.slice(0, Math.max(0, 12 - form.gallery.length))) {
          const result = await adminUpload(file);
          urls.push(result.url);
        }
        setForm((current) => ({ ...current, gallery: [...current.gallery, ...urls].slice(0, 12) }));
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-softwhite text-charcoal">
      <Toaster position="bottom-center" />
      <header className="border-b border-line bg-white/90">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-4">
          <Link to="/"><img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="h-6 w-auto" /></Link>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin/resources" className={buttonClass}>{t.resources}</Link>
            <Link to="/admin/saas-explainers" className={buttonClass}>SaaS Explainers</Link>
            <Link to="/" className={buttonClass}>{t.site}</Link>
            <LanguageSwitcher />
            <button type="button" className={buttonClass} onClick={() => { clearToken(); navigate("/admin/login"); }}><LogOut size={16} />{t.logout}</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="font-display text-3xl font-bold">{t.title}</h1><p className="mt-2 text-sm text-graphite/60">{t.subtitle}</p></div>
          <button type="button" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 text-sm font-semibold text-white"><Plus size={16} />{t.add}</button>
        </div>
        <div className="mt-8 grid gap-4">
          {projects === null && <p className="py-12 text-center text-graphite/60">{t.loading}</p>}
          {projects?.length === 0 && <p className="rounded-3xl border border-dashed border-line bg-white py-12 text-center text-graphite/60">{t.empty}</p>}
          {projects?.map((project) => (
            <article key={project.id} className="flex flex-wrap items-center gap-4 rounded-3xl border border-line bg-white p-4 shadow-card">
              {project.cover_url ? <img src={project.cover_url} alt="" className="h-20 w-32 rounded-2xl object-cover" /> : <div className="h-20 w-32 rounded-2xl bg-mist" />}
              <div className="min-w-0 flex-1">
                <h2 className="font-bold">{project[`title_${lang}`] || project.title_en || project.title_ar}</h2>
                <p className="mt-1 break-all text-xs text-graphite/50">/projects/{project.slug}</p>
                <div className="mt-2 flex gap-2 text-xs"><span>{project.published ? t.published : t.draft}</span>{project.featured && <span>· {t.featured}</span>}{project.archived && <span>· {t.archived}</span>}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {project.published && !project.archived && <Link to={`/projects/${project.slug}`} title={t.site} className={buttonClass}><ExternalLink size={16} /></Link>}
                <button type="button" title={project.published ? t.draft : t.published} className={buttonClass} onClick={() => patch(project, { published: !project.published })}>{project.published ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                <button type="button" title={t.featured} className={buttonClass} onClick={() => patch(project, { featured: !project.featured })}>{project.featured ? <StarOff size={16} /> : <Star size={16} />}</button>
                <button type="button" title={t.archived} className={buttonClass} onClick={() => patch(project, { archived: !project.archived })}>{project.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}</button>
                <button type="button" title={t.edit} className={buttonClass} onClick={() => openEdit(project)}><Pencil size={16} /></button>
                <button type="button" title={t.deleted} className={buttonClass} onClick={() => remove(project)}><Trash2 size={16} /></button>
              </div>
            </article>
          ))}
        </div>
      </main>
      {modal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-3" onMouseDown={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
        <div role="dialog" aria-modal="true" aria-label={modal.mode === "create" ? t.new : t.edit} className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-softwhite p-5 shadow-soft md:p-8">
          <div className="flex items-center justify-between"><h2 className="font-display text-2xl font-bold">{modal.mode === "create" ? t.new : t.edit}</h2><button type="button" onClick={() => setModal(null)} aria-label={t.cancel}><X size={20} /></button></div>
          <form onSubmit={save} className="mt-6 space-y-6">
            <label className="block text-sm font-semibold">{t.slug}<input dir="ltr" className={`${inputClass} mt-2`} required pattern="[a-z0-9]+(-[a-z0-9]+)*" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} /></label>
            <p className="-mt-4 text-xs text-graphite/55">{t.slugHint}</p>
            <div className="flex flex-wrap gap-2 border-b border-line pb-3">{LANGUAGES.map((language) => <button key={language.id} type="button" onClick={() => setEditLang(language.id)} className={`rounded-full px-4 py-2 text-sm ${editLang === language.id ? "bg-charcoal text-white" : "bg-white text-graphite"}`}>{language.label}</button>)}</div>
            <div className="grid gap-4 sm:grid-cols-2">
              {LOCAL_FIELDS.map((field) => <label key={`${field}_${editLang}`} className={`block text-sm font-semibold ${["statement", "problem", "solution", "outcome"].includes(field) ? "sm:col-span-2" : ""}`}>{t[field === "title" ? "titleField" : field]} {field === "title" && editLang !== "fr" ? "*" : ""}
                {["title", "type"].includes(field) ? <input dir={editLang === "ar" ? "rtl" : "ltr"} className={`${inputClass} mt-2`} required={field === "title" && editLang !== "fr"} value={form[`${field}_${editLang}`]} onChange={(e) => setForm({ ...form, [`${field}_${editLang}`]: e.target.value, ...(field === "title" && editLang === "en" && !form.slug ? { slug: slugify(e.target.value) } : {}) })} />
                  : <textarea dir={editLang === "ar" ? "rtl" : "ltr"} rows={field === "statement" ? 2 : 3} className={`${inputClass} mt-2`} value={form[`${field}_${editLang}`]} onChange={(e) => setForm({ ...form, [`${field}_${editLang}`]: e.target.value })} />}
              </label>)}
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div><p className="mb-2 text-sm font-semibold">{t.cover}</p><label className={buttonClass}>{uploading ? t.saving : t.upload}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => upload(e, "cover")} /></label>{form.cover_url && <div className="mt-3"><img src={form.cover_url} alt="" className="h-36 w-full rounded-xl object-cover" /><button type="button" className="mt-2 text-xs text-red-600" onClick={() => setForm({ ...form, cover_url: "" })}>{t.removeImage}</button></div>}</div>
              <div><p className="mb-2 text-sm font-semibold">{t.gallery} ({form.gallery.length}/12)</p><label className={buttonClass}>{uploading ? t.saving : t.uploadMore}<input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading || form.gallery.length >= 12} onChange={(e) => upload(e, "gallery")} /></label><div className="mt-3 grid grid-cols-3 gap-2">{form.gallery.map((url, index) => <div key={url} className="relative"><img src={url} alt="" className="h-24 w-full rounded-xl object-cover" /><button type="button" aria-label={t.removeImage} className="absolute end-1 top-1 rounded-full bg-white p-1" onClick={() => setForm((current) => ({ ...current, gallery: current.gallery.filter((_, i) => i !== index) }))}><X size={14} /></button></div>)}</div></div>
            </div>
            <label className="block text-sm font-semibold">{t.external}<input dir="ltr" type="url" placeholder="https://" className={`${inputClass} mt-2`} value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} /></label>
            <div className="flex flex-wrap gap-5 text-sm">{["published", "featured", "archived"].map((field) => <label key={field} className="flex items-center gap-2"><input type="checkbox" checked={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.checked })} />{t[field]}</label>)}</div>
            <div className="flex justify-end gap-3"><button type="button" className={buttonClass} onClick={() => setModal(null)}>{t.cancel}</button><button type="submit" disabled={saving || uploading} className="rounded-full bg-charcoal px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{saving ? t.saving : t.save}</button></div>
          </form>
        </div>
      </div>}
    </div>
  );
};

export default AdminProjects;
